import { useState } from "react";
import { useForm } from "react-hook-form";
import { Alert, Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { MdOutlineLock, MdOutlineBadge } from "react-icons/md";
import axios from "../config/axiosInstance";
import { AUTH } from "../config/apiConfig";
import { useApplication } from "../contexts/ApplicationContext";
import { Card, SectionTitle } from "../components/crm/Primitives";
import {
  SkeletonHeader,
  SkeletonPanel,
} from "../components/crm/Skeletons";
import { Badge } from "../components/crm/Primitives";
import { initialsOf } from "../components/crm/crmHelpers";

/*
  Perfil do staff da Relinq.

  Esta tela foi REESCRITA por causa de um bug de dados, não de layout.

  A versão anterior buscava `GET /professional/{id}` usando o id do usuário do
  BACK-OFFICE. São tabelas diferentes: `Users` (staff da Relinq) e
  `Professionals` (funcionária de salão). Com ids que começam em 1 nas duas,
  o staff #1 abria a ficha completa — CPF, endereço, comissões — de uma
  profissional de salão qualquer, e o botão "salvar" ESCREVIA os dados do
  formulário em cima do cadastro dela.

  Agora a identidade vem de `GET /me`, a mesma fonte que o servidor usa para
  autorizar. Nome, telefone e papel são exibidos sem edição: alterá-los é ação
  de administrador, em Acessos da equipe (`PUT /staff/:id`) — e um
  ACCOUNT_MANAGER que pudesse editar o próprio registro poderia se promover a
  ADMIN. A senha é o que a pessoa muda sozinha, por `PUT /me/password`.
*/

const MIN_PASSWORD_LENGTH = 6;

const ROLE_LABEL = {
  ADMIN: "Administrador",
  ACCOUNT_MANAGER: "Gerente de contas",
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
      {label}
    </p>
    <p className="text-sm font-medium text-slate-800">{value || "—"}</p>
  </div>
);

const MyProfilePage = () => {
  const { user, role, isLoadingUser } = useApplication();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const onSubmitPassword = async (data) => {
    setAlert(null);

    if (data.password !== data.passwordConfirm) {
      setAlert({ type: "failure", message: "As senhas não conferem." });
      return;
    }

    try {
      setIsSubmitting(true);
      await axios.put(AUTH.CHANGE_PASSWORD, { password: data.password });
      reset();
      setAlert({ type: "success", message: "Senha atualizada com sucesso!" });
    } catch (error) {
      setAlert({
        type: "failure",
        message:
          error.response?.data?.message || "Não foi possível atualizar a senha.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoadingUser) {
    return (
      <div className="space-y-4">
        <SkeletonHeader withAction={false} />
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <SkeletonPanel lines={4} />
          <SkeletonPanel lines={3} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <header className="flex items-center gap-3">
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-pink-50 text-sm font-bold text-pink-700">
          {initialsOf(user?.name)}
        </span>
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold text-slate-900">
            {user?.name || "Meu perfil"}
          </h1>
          <div className="mt-0.5 flex items-center gap-2">
            <Badge tone={role === "ADMIN" ? "info" : "neutral"}>
              {ROLE_LABEL[role] || role}
            </Badge>
            <span className="text-xs text-slate-500">{user?.email}</span>
          </div>
        </div>
      </header>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <section>
          <SectionTitle
            title="Seus dados"
            hint="Alterações de nome, papel e carteira são feitas por um administrador em Acessos da equipe"
          />
          <Card className="p-4">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Nome" value={user?.name} />
              <Field label="E-mail" value={user?.email} />
              <Field label="Papel" value={ROLE_LABEL[role] || role} />
              <Field
                label="Situação"
                value={user?.isActive ? "Ativo" : "Inativo"}
              />
            </div>

            <p className="mt-4 flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-xs text-slate-600">
              <MdOutlineBadge size={16} className="mt-0.5 shrink-0" />
              {role === "ADMIN"
                ? "Como administrador, você enxerga a base inteira de clientes."
                : "Como gerente de contas, você enxerga apenas os clientes da sua carteira."}
            </p>
          </Card>
        </section>

        <section>
          <SectionTitle
            title="Trocar senha"
            hint="Vale só para a sua própria conta"
          />
          <Card className="p-4">
            {alert ? (
              <Alert color={alert.type} className="mb-3">
                {alert.message}
              </Alert>
            ) : null}

            <form
              onSubmit={handleSubmit(onSubmitPassword)}
              className="space-y-3"
            >
              <div>
                <label
                  htmlFor="password"
                  className="mb-1 block text-xs font-semibold text-slate-700"
                >
                  Nova senha
                </label>
                <input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  {...register("password", {
                    required: "Informe a nova senha",
                    minLength: {
                      value: MIN_PASSWORD_LENGTH,
                      message: `Use pelo menos ${MIN_PASSWORD_LENGTH} caracteres`,
                    },
                  })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                />
                {errors.password ? (
                  <p className="mt-1 text-xs text-rose-600">
                    {errors.password.message}
                  </p>
                ) : null}
              </div>

              <div>
                <label
                  htmlFor="passwordConfirm"
                  className="mb-1 block text-xs font-semibold text-slate-700"
                >
                  Confirme a nova senha
                </label>
                <input
                  id="passwordConfirm"
                  type="password"
                  autoComplete="new-password"
                  {...register("passwordConfirm", {
                    required: "Confirme a nova senha",
                  })}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
                />
                {errors.passwordConfirm ? (
                  <p className="mt-1 text-xs text-rose-600">
                    {errors.passwordConfirm.message}
                  </p>
                ) : null}
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-pink-600 hover:bg-pink-700"
              >
                {isSubmitting ? (
                  <AiOutlineLoading className="mr-2 animate-spin" size={16} />
                ) : (
                  <MdOutlineLock className="mr-2" size={16} />
                )}
                {isSubmitting ? "Salvando..." : "Atualizar senha"}
              </Button>
            </form>
          </Card>
        </section>
      </div>
    </div>
  );
};

export default MyProfilePage;
